import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  useTheme,
  Tooltip,
  IconButton,
  Fade,
  Chip,
  Button,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  CompareArrows as CompareIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Componente estilizado para células da tabela
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.9rem',
  padding: '16px 24px',
  '&.feature-name': {
    fontWeight: 500,
  },
  '&.plan-header': {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    borderBottom: `2px solid ${theme.palette.primary.main}`,
  },
  '&.premium-header': {
    color: theme.palette.secondary.main,
    borderBottom: `2px solid ${theme.palette.secondary.main}`,
  },
  '&.free-header': {
    color: '#6c757d',
    borderBottom: `2px solid #6c757d`,
  },
}));

// Componente estilizado para linhas da tabela
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
  },
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  // Esconde a última borda
  '&:last-child td, &:last-child th': {
    borderBottom: 0,
  },
}));

// Interface para as características dos planos
interface FeatureDetail {
  name: string;
  free: boolean | number | string;
  basic: boolean | number | string;
  premium: boolean | number | string;
  tooltip: string;
  highlight?: boolean;
  category: string;
}

// Componente para comparação de planos
const PlanComparison: React.FC = () => {
  const theme = useTheme();
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Lista de categorias de recursos
  const categories = [
    { id: 'all', name: 'Todos os Recursos' },
    { id: 'general', name: 'Recursos Gerais' },
    { id: 'whatsapp', name: 'WhatsApp IA Bot' },
    { id: 'email', name: 'E-mail Response' },
    { id: 'social', name: 'Redes Sociais' },
  ];

  // Lista de características dos planos
  const features: FeatureDetail[] = [
    // Recursos Gerais
    { name: 'Leads', free: 50, basic: 200, premium: 'Ilimitado', tooltip: 'Número máximo de leads que podem ser gerenciados', highlight: true, category: 'general' },
    { name: 'Produtos', free: 20, basic: 50, premium: 'Ilimitado', tooltip: 'Número máximo de produtos que podem ser cadastrados', highlight: true, category: 'general' },
    { name: 'Usuários', free: 1, basic: 3, premium: 10, tooltip: 'Número de usuários que podem acessar a conta', category: 'general' },
    { name: 'Suporte Técnico', free: 'E-mail', basic: 'Prioritário', premium: '24/7', tooltip: 'Tipo de suporte técnico disponível', category: 'general' },
    { name: 'Relatórios Personalizados', free: false, basic: true, premium: true, tooltip: 'Capacidade de criar relatórios personalizados', category: 'general' },
    { name: 'Exportação de Dados', free: false, basic: true, premium: true, tooltip: 'Capacidade de exportar dados em diferentes formatos', category: 'general' },
    { name: 'API de Integração', free: false, basic: false, premium: true, tooltip: 'Acesso à API para integração com outros sistemas', category: 'general' },
    
    // WhatsApp IA Bot
    { name: 'WhatsApp IA Bot', free: true, basic: true, premium: true, tooltip: 'Acesso ao bot de IA para WhatsApp', highlight: true, category: 'whatsapp' },
    { name: 'Mensagens por Mês', free: 100, basic: 1000, premium: 'Ilimitado', tooltip: 'Número máximo de mensagens processadas pelo bot por mês', category: 'whatsapp' },
    { name: 'Respostas Automatizadas', free: true, basic: true, premium: true, tooltip: 'Capacidade de configurar respostas automáticas', category: 'whatsapp' },
    { name: 'Detecção de Intenções', free: 'Básica', basic: 'Avançada', premium: 'Premium', tooltip: 'Nível de sofisticação na detecção de intenções do usuário', category: 'whatsapp' },
    { name: 'Integração com Produtos', free: true, basic: true, premium: true, tooltip: 'Capacidade de integrar produtos ao bot', category: 'whatsapp' },
    { name: 'Captura de Leads', free: true, basic: true, premium: true, tooltip: 'Capacidade de capturar leads através do WhatsApp', category: 'whatsapp' },
    { name: 'Base de Conhecimento (RAG)', free: '5 docs', basic: '20 docs', premium: 'Ilimitado', tooltip: 'Tamanho da base de conhecimento para contextualizar respostas', highlight: true, category: 'whatsapp' },
    { name: 'Histórico de Conversas', free: '7 dias', basic: '30 dias', premium: '365 dias', tooltip: 'Tempo de retenção do histórico de conversas', category: 'whatsapp' },
    
    // E-mail Response
    { name: 'E-mail Response com IA', free: true, basic: true, premium: true, tooltip: 'Acesso ao assistente de e-mail com IA', highlight: true, category: 'email' },
    { name: 'E-mails por Mês', free: 50, basic: 500, premium: 'Ilimitado', tooltip: 'Número máximo de e-mails processados por mês', category: 'email' },
    { name: 'Classificação Automática', free: true, basic: true, premium: true, tooltip: 'Classificação automática de e-mails por categoria/intenção', category: 'email' },
    { name: 'Base de Conhecimento (RAG)', free: '3 docs', basic: '15 docs', premium: 'Ilimitado', tooltip: 'Tamanho da base de conhecimento para contextualizar respostas', category: 'email' },
    { name: 'Automação de E-mails', free: false, basic: true, premium: true, tooltip: 'Capacidade de configurar envio automático de e-mails', category: 'email' },
    { name: 'Regras de Resposta', free: 2, basic: 10, premium: 'Ilimitado', tooltip: 'Número de regras de resposta automática que podem ser configuradas', category: 'email' },
    { name: 'Monitoramento de E-mails', free: false, basic: true, premium: true, tooltip: 'Monitoramento de métricas de e-mail', category: 'email' },
    
    // Redes Sociais
    { name: 'Gestão de Redes Sociais', free: true, basic: true, premium: true, tooltip: 'Acesso ao sistema de gestão de redes sociais', highlight: true, category: 'social' },
    { name: 'Plataformas Suportadas', free: 2, basic: 4, premium: 'Todas', tooltip: 'Número de plataformas de redes sociais suportadas', category: 'social' },
    { name: 'Calendário de Conteúdo', free: true, basic: true, premium: true, tooltip: 'Acesso ao calendário de conteúdo', category: 'social' },
    { name: 'Posts Agendados', free: 10, basic: 50, premium: 'Ilimitado', tooltip: 'Número máximo de posts que podem ser agendados', category: 'social' },
    { name: 'Analytics Avançado', free: false, basic: true, premium: true, tooltip: 'Acesso a métricas avançadas de desempenho', category: 'social' },
    { name: 'Biblioteca de Mídia', free: '100MB', basic: '1GB', premium: '10GB', tooltip: 'Espaço de armazenamento para a biblioteca de mídia', category: 'social' },
    { name: 'Assistente de IA', free: false, basic: true, premium: true, tooltip: 'Acesso ao assistente de IA para geração de conteúdo', category: 'social' },
    { name: 'Sugestão de Hashtags', free: false, basic: true, premium: true, tooltip: 'Sugestão automática de hashtags relevantes', category: 'social' },
  ];

  // Filtrar características com base na categoria selecionada e na opção de mostrar todos
  const filteredFeatures = features.filter(feature => {
    if (selectedCategory !== 'all' && feature.category !== selectedCategory) {
      return false;
    }
    
    if (!showAllFeatures && !feature.highlight) {
      return false;
    }
    
    return true;
  });

  // Renderizar valor da característica
  const renderFeatureValue = (value: boolean | number | string) => {
    if (typeof value === 'boolean') {
      return value ? (
        <CheckIcon sx={{ color: theme.palette.success.main }} />
      ) : (
        <CloseIcon sx={{ color: theme.palette.error.main }} />
      );
    } else if (value === 'Ilimitado') {
      return (
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 'bold', 
            color: theme.palette.success.main,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <CheckIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
          {value}
        </Typography>
      );
    } else {
      return <Typography variant="body2">{value}</Typography>;
    }
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        borderRadius: '16px', 
        mb: 4,
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      <Box sx={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        right: 0, 
        height: '5px', 
        background: 'linear-gradient(90deg, #3f51b5, #f50057)'
      }} />
      
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 0 } }}>
          <CompareIcon sx={{ color: theme.palette.primary.main, mr: 1.5, fontSize: '2rem' }} />
          <Typography variant="h5" fontWeight="bold">
            Comparativo de Planos
          </Typography>
          <Tooltip 
            title="Compare todos os recursos disponíveis em cada plano" 
            placement="top"
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 600 }}
          >
            <IconButton size="small" sx={{ ml: 1 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mr: 2 }}>
            {categories.map(category => (
              <Chip 
                key={category.id}
                label={category.name}
                onClick={() => setSelectedCategory(category.id)}
                color={selectedCategory === category.id ? "primary" : "default"}
                variant={selectedCategory === category.id ? "filled" : "outlined"}
                sx={{ fontWeight: selectedCategory === category.id ? 'bold' : 'normal' }}
              />
            ))}
          </Box>
          
          <FormControlLabel
            control={
              <Switch 
                checked={showAllFeatures}
                onChange={(e) => setShowAllFeatures(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                Mostrar todos os recursos
              </Typography>
            }
          />
        </Box>
      </Box>
      
      <TableContainer>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <StyledTableCell className="feature-name">Recurso</StyledTableCell>
              <StyledTableCell align="center" className="free-header">
                Free (Gratuito)
              </StyledTableCell>
              <StyledTableCell align="center" className="plan-header">
                Intermediário
              </StyledTableCell>
              <StyledTableCell align="center" className="premium-header">
                Premium
                <StarIcon sx={{ ml: 0.5, fontSize: '0.9rem', verticalAlign: 'middle' }} />
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFeatures.map((feature) => (
              <StyledTableRow key={feature.name}>
                <StyledTableCell 
                  component="th" 
                  scope="row" 
                  className="feature-name"
                  sx={{ 
                    fontWeight: feature.highlight ? 'bold' : 'normal',
                    color: feature.highlight ? theme.palette.primary.main : 'inherit'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {feature.name}
                    <Tooltip 
                      title={feature.tooltip} 
                      placement="top"
                      TransitionComponent={Fade}
                      TransitionProps={{ timeout: 600 }}
                    >
                      <IconButton size="small" sx={{ ml: 0.5 }}>
                        <InfoIcon fontSize="small" sx={{ fontSize: '0.9rem', opacity: 0.7 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </StyledTableCell>
                <StyledTableCell align="center">
                  {renderFeatureValue(feature.free)}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {renderFeatureValue(feature.basic)}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {renderFeatureValue(feature.premium)}
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Button 
          variant="outlined" 
          color="primary"
          sx={{ 
            fontWeight: 'medium',
            textTransform: 'none',
            borderRadius: '8px',
            px: 3
          }}
        >
          Ver Plano Intermediário
        </Button>
        <Button 
          variant="contained" 
          color="secondary"
          sx={{ 
            fontWeight: 'medium',
            textTransform: 'none',
            borderRadius: '8px',
            px: 3
          }}
        >
          Ver Plano Premium
        </Button>
      </Box>
    </Paper>
  );
};

export default PlanComparison;
